import React, { useEffect, useState } from "react";
import { callApi } from "../../api/callApi";
import { API_ENDPOINT, API_URL_2 } from "../../api/constant";
import { IProduct, ProductInfo } from "../../interfaces/product";
import { toFixedThree } from "../../utility/helper";

interface ProductVariantSelectorProps {
    onSelect: (product: IProduct | null, productInfo: ProductInfo | null) => void;
    selectedProductId?: string | null;
    selectedProductInfoId?: string | null;
}

/**
 * Reusable "search product -> pick product -> pick variant (ProductInfo)" workflow.
 * Search behaviour mirrors the product search used in AddMeateryBox.
 */
const ProductVariantSelector: React.FC<ProductVariantSelectorProps> = ({
    onSelect,
    selectedProductId,
    selectedProductInfoId,
}) => {
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [products, setProducts] = useState<IProduct[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [showResults, setShowResults] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<IProduct | null>(null);

    // debounce search
    useEffect(() => {
        if (searchTerm.trim().length < 3) {
            setProducts([]);
            return;
        }
        const handler = setTimeout(() => fetchProducts(searchTerm), 400);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const fetchProducts = async (query: string) => {
        try {
            setLoading(true);
            const res = await callApi(
                `${API_URL_2}${API_ENDPOINT.page}products?title=${encodeURIComponent(query)}`,
                { method: "GET" }
            );
            const list: IProduct[] = Array.isArray(res) ? res : res?.data || [];
            // Only linkable to real (non-combo) products with variants
            setProducts(list.filter((p) => !p.isCombo));
            setShowResults(true);
        } catch (err) {
            console.error("ProductVariantSelector fetchProducts error", err);
            setProducts([]);
        } finally {
            setLoading(false);
        }
    };

    const handlePickProduct = (product: IProduct) => {
        setSelectedProduct(product);
        setShowResults(false);
        setSearchTerm(product.title);
        // Reset variant selection until admin picks one
        onSelect(product, null);
    };

    const handlePickVariant = (productInfoId: string) => {
        if (!selectedProduct) return;
        const info = selectedProduct.productInfo?.find((v) => v.id === productInfoId) || null;
        onSelect(selectedProduct, info);
    };

    const handleClear = () => {
        setSelectedProduct(null);
        setSearchTerm("");
        setProducts([]);
        onSelect(null, null);
    };

    return (
        <div className="product-variant-selector">
            {/* Step 1: Search product */}
            <label className="form-label">Search Product</label>
            <div className="position-relative">
                <div className="input-group">
                    <input
                        type="text"
                        className="form-control"
                        placeholder="Type at least 3 characters to search..."
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setSelectedProduct(null);
                        }}
                        onFocus={() => products.length > 0 && setShowResults(true)}
                    />
                    {(selectedProduct || searchTerm) && (
                        <button type="button" className="btn btn-outline-secondary" onClick={handleClear}>
                            <i className="bi bi-x-lg" />
                        </button>
                    )}
                </div>

                {showResults && (
                    <div
                        className="list-group position-absolute w-100 shadow-sm"
                        style={{ zIndex: 5, maxHeight: 260, overflowY: "auto" }}
                    >
                        {loading ? (
                            <div className="list-group-item text-muted">Searching...</div>
                        ) : products.length === 0 ? (
                            <div className="list-group-item text-muted">No products found</div>
                        ) : (
                            products.map((product) => (
                                <button
                                    type="button"
                                    key={product.id}
                                    className="list-group-item list-group-item-action d-flex align-items-center gap-2"
                                    onClick={() => handlePickProduct(product)}
                                >
                                    <img
                                        className="flex-shrink-0"
                                        src={product.images?.[0]}
                                        alt={product.title}
                                        style={{ width: 36, height: 36, objectFit: "cover", borderRadius: 4 }}
                                    />
                                    <div className="text-start" style={{ minWidth: 0 }}>
                                        <div className="fw-semibold text-break">{product.title}</div>
                                        <div className="text-muted small">{product.productCode}</div>
                                    </div>
                                </button>
                            ))
                        )}
                    </div>
                )}
            </div>

            {/* Step 2: Select variant */}
            {selectedProduct && (
                <div className="mt-3">
                    <label className="form-label">Select Variant</label>
                    <select
                        className="form-control"
                        value={selectedProductInfoId || ""}
                        onChange={(e) => handlePickVariant(e.target.value)}
                    >
                        <option value="">Select a variant</option>
                        {selectedProduct.productInfo?.map((variant) => (
                            <option key={variant.id} value={variant.id}>
                                {variant.pieceWeight} — BHD {toFixedThree(variant.price)} (Stock: {variant.quantity})
                            </option>
                        ))}
                    </select>
                    {(!selectedProduct.productInfo || selectedProduct.productInfo.length === 0) && (
                        <small className="text-danger">This product has no variants.</small>
                    )}
                </div>
            )}

            {selectedProductId && !selectedProduct && (
                <small className="text-muted d-block mt-2">Linked product selected.</small>
            )}
        </div>
    );
};

export default ProductVariantSelector;
